// Utility functions for generating heatmap data from station readings

/**
 * Calculate distance between two points (Haversine formula)
 */
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // Distance in meters
};

/**
 * Interpolate value at a point using inverse distance weighting
 */
const interpolateValueAtPoint = (lat, lon, stations, dataType, maxDistance = 2000) => {
  let totalWeight = 0;
  let weightedValue = 0;

  stations.forEach((station) => {
    if (!station.currentReadings || !station.isActive) return;

    const distance = calculateDistance(lat, lon, station.locationLat, station.locationLon);
    
    if (distance > maxDistance) return;

    let value = 0;
    switch (dataType) {
      case 'temperature':
        value = station.currentReadings.temperature || 0;
        break;
      case 'windSpeed':
        value = station.currentReadings.windSpeed || 0;
        break;
      case 'humidity':
        value = station.currentReadings.humidity || 0;
        break;
      case 'solarRadiation':
        value = (station.currentReadings.solarRadiation || 0) / 10;
        break;
      default:
        return;
    }

    if (value <= 0) return;

    // Inverse distance weighting with power of 2 for smoother interpolation
    const weight = 1 / (Math.pow(distance / 1000, 2) + 0.1); // +0.1 to avoid division by zero

    weightedValue += value * weight;
    totalWeight += weight;
  });

  if (totalWeight === 0) {
    return null;
  }

  return weightedValue / totalWeight;
};

/**
 * Generate heatmap data ONLY at station locations
 * Shows actual measured values at each station point
 * Returns array of [lat, lon, intensity] tuples
 * @param {Array} stations - Array of station objects
 * @param {string} dataType - Type of data to visualize
 */
export const generateStationBasedHeatmapData = (stations, dataType) => {
  const points = [];
  
  if (!stations || stations.length === 0) return points;

  // Generate heatmap point ONLY at each station location
  stations.forEach(station => {
    if (!station.currentReadings || !station.isActive) return;
    
    const stationLat = station.locationLat;
    const stationLon = station.locationLon;
    
    let intensity = 0;
    switch (dataType) {
      case 'temperature':
        intensity = station.currentReadings.temperature || 0;
        break;
      case 'windSpeed':
        intensity = station.currentReadings.windSpeed || 0;
        break;
      case 'windDirection':
        intensity = station.currentReadings.windSpeed || 0;
        break;
      case 'humidity':
        intensity = station.currentReadings.humidity || 0;
        break;
      case 'solarRadiation':
        intensity = (station.currentReadings.solarRadiation || 0) / 10;
        break;
      default:
        return;
    }
    
    if (intensity > 0) {
      // Add point ONLY at the station location
      points.push([stationLat, stationLon, intensity]);
    }
  });
  
  return points;
};

/**
 * Convert station data to heatmap points format
 * Returns array of [lat, lon, intensity] tuples
 * Now uses station-based heatmaps to show individual station differences
 */
export const generateHeatmapData = (stations, dataType, interpolate = true) => {
  // Show heatmap ONLY at station locations (actual measurement points)
  // No interpolation - just the measured values at each station
  return generateStationBasedHeatmapData(stations, dataType);
};

/**
 * Get color gradient configuration for different data types
 * Professional meteorological-style gradients with smooth transitions
 */
export const getHeatmapGradient = (dataType) => {
  switch (dataType) {
    case 'temperature':
      // Professional temperature gradient: deep blue (cold) → teal → green → yellow → orange → red (hot)
      // Inspired by NOAA and meteorological standards
      return {
        0.0: '#0a1f5c',    // Deep navy (very cold)
        0.1: '#1e3a8a',    // Dark blue
        0.2: '#2563eb',    // Blue
        0.3: '#0891b2',    // Teal/cyan
        0.4: '#14b8a6',    // Teal
        0.5: '#22c55e',    // Green (moderate)
        0.6: '#84cc16',    // Lime
        0.7: '#eab308',    // Yellow (warm)
        0.8: '#f97316',    // Orange
        0.9: '#ef4444',    // Red (hot)
        1.0: '#dc2626'     // Dark red (very hot)
      };
    case 'windSpeed':
      // Professional wind gradient: calm white → aqua → blue → violet → magenta (strong)
      return {
        0.0: 'rgba(255,255,255,0.3)',  // Almost transparent (calm)
        0.1: '#e0f7fa',    // Very light cyan
        0.2: '#80deea',    // Light cyan
        0.3: '#26c6da',    // Cyan
        0.4: '#00acc1',    // Dark cyan
        0.5: '#0288d1',    // Blue
        0.6: '#1565c0',    // Dark blue
        0.7: '#5e35b1',    // Deep purple
        0.8: '#8e24aa',    // Purple
        0.9: '#d81b60',    // Pink/magenta
        1.0: '#c2185b'     // Dark magenta (very strong)
      };
    case 'windDirection':
      // Same as wind speed for visual consistency
      return {
        0.0: 'rgba(255,255,255,0.3)',
        0.1: '#e0f7fa',
        0.2: '#80deea',
        0.3: '#26c6da',
        0.4: '#00acc1',
        0.5: '#0288d1',
        0.6: '#1565c0',
        0.7: '#5e35b1',
        0.8: '#8e24aa',
        0.9: '#d81b60',
        1.0: '#c2185b'
      };
    case 'humidity':
      // Professional humidity gradient: warm tan (dry) → green → blue-green (humid)
      return {
        0.0: '#fef3c7',    // Light amber (very dry)
        0.1: '#fde68a',    // Amber
        0.2: '#d9f99d',    // Light lime
        0.3: '#a3e635',    // Lime
        0.4: '#65a30d',    // Dark lime
        0.5: '#22c55e',    // Green
        0.6: '#10b981',    // Emerald
        0.7: '#14b8a6',    // Teal
        0.8: '#06b6d4',    // Cyan
        0.9: '#0891b2',    // Dark cyan
        1.0: '#0e7490'     // Deep teal (very humid)
      };
    case 'solarRadiation':
      // Professional solar gradient: purple (night/low) → blue → yellow → orange → red (intense)
      return {
        0.0: '#312e81',    // Deep indigo (no radiation)
        0.1: '#4338ca',    // Indigo
        0.2: '#3b82f6',    // Blue
        0.3: '#06b6d4',    // Cyan
        0.4: '#fef08a',    // Light yellow
        0.5: '#fde047',    // Yellow
        0.6: '#facc15',    // Gold
        0.7: '#f59e0b',    // Amber
        0.8: '#f97316',    // Orange
        0.9: '#ef4444',    // Red
        1.0: '#dc2626'     // Dark red (intense radiation)
      };
    default:
      return {
        0.0: '#1e3a8a',
        0.25: '#22c55e',
        0.5: '#eab308',
        0.75: '#f97316',
        1.0: '#dc2626'
      };
  }
};

/**
 * Get radius configuration for different data types
 * Moderate radius to create isolated circular blobs without angular overlap
 */
export const getHeatmapRadius = (dataType) => {
  switch (dataType) {
    case 'temperature':
      return 50; // Moderate radius for distinct temperature zones
    case 'windSpeed':
      return 45; // Moderate for wind visualization
    case 'windDirection':
      return 45;
    case 'humidity':
      return 50; // Moderate for humidity zones
    case 'solarRadiation':
      return 55; // Slightly larger for solar radiation
    default:
      return 45;
  }
};

/**
 * Get blur configuration for different data types
 * High blur relative to radius for soft circular edges
 */
export const getHeatmapBlur = (dataType) => {
  switch (dataType) {
    case 'temperature':
      return 40; // High blur for soft circular edges
    case 'windSpeed':
      return 38; // High blur for smooth wind zones
    case 'windDirection':
      return 38;
    case 'humidity':
      return 40; // High blur for soft humidity zones
    case 'solarRadiation':
      return 45; // High blur for smooth solar zones
    default:
      return 38;
  }
};

/**
 * Get max value for normalization (optional, can be auto-calculated)
 */
export const getHeatmapMax = (dataType) => {
  switch (dataType) {
    case 'temperature':
      return 40; // Max temperature in Celsius (slightly higher for better range)
    case 'windSpeed':
      return 25; // Max wind speed in m/s (higher for better visualization)
    case 'windDirection':
      return 25; // Same as wind speed
    case 'humidity':
      return 100; // Max humidity percentage
    case 'solarRadiation':
      return 1200; // Max solar radiation in W/m² (higher for better range)
    default:
      return null; // Auto-calculate
  }
};

/**
 * Get min value for normalization
 */
export const getHeatmapMin = (dataType) => {
  switch (dataType) {
    case 'temperature':
      return 0; // Min temperature in Celsius
    case 'windSpeed':
      return 0; // Min wind speed
    case 'windDirection':
      return 0;
    case 'humidity':
      return 0; // Min humidity percentage
    case 'solarRadiation':
      return 0; // Min solar radiation
    default:
      return 0;
  }
};

/**
 * Get unit label for data type
 */
export const getHeatmapUnit = (dataType) => {
  switch (dataType) {
    case 'temperature':
      return '°C';
    case 'windSpeed':
      return 'm/s';
    case 'windDirection':
      return '°';
    case 'humidity':
      return '%';
    case 'solarRadiation':
      return 'W/m²';
    default:
      return '';
  }
};

/**
 * Get display name for data type
 */
export const getHeatmapLabel = (dataType) => {
  switch (dataType) {
    case 'temperature':
      return 'Temperature';
    case 'windSpeed':
      return 'Wind Speed';
    case 'windDirection':
      return 'Wind Direction';
    case 'humidity':
      return 'Humidity';
    case 'solarRadiation':
      return 'Solar Radiation';
    default:
      return 'Heatmap';
  }
};

/**
 * Generate wind direction vectors for vector field visualization
 * Returns array of {lat, lon, direction, speed} objects
 */
export const generateWindVectors = (stations) => {
  const vectors = [];
  
  stations.forEach(station => {
    if (!station.currentReadings || !station.isActive) return;
    
    // Note: We don't have wind direction in current readings, so we'll simulate it
    // In a real implementation, this would come from station.currentReadings.windDirection
    const windSpeed = station.currentReadings.windSpeed || 0;
    if (windSpeed > 0) {
      // Simulate wind direction (0-360 degrees, where 0 is North)
      // In real data, this would be station.currentReadings.windDirection
      const windDirection = Math.random() * 360;
      
      vectors.push({
        lat: station.locationLat,
        lon: station.locationLon,
        direction: windDirection,
        speed: windSpeed
      });
    }
  });
  
  return vectors;
};

